import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/auth/AuthProvider';
import { I18nProvider } from '@/i18n/I18nProvider';
import { ThemeProvider } from '@/theme/ThemeProvider';
import { ToastProvider } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { App } from './App';
import '@/lib/install';
import { installPreloadRecovery, registerServiceWorker } from '@/lib/app-update';
import { installErrorReporter } from '@/lib/client-errors';
import './styles/fonts.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';
import './styles/layout.css';
import './styles/calendar.css';
import './styles/maps.css';
import './styles/landing.css';
import './styles/games.css';
import './styles/kid.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, error) => {
        // لا نعيد المحاولة على رفض صلاحية أو مخالفة قاعدة — الرد نهائي
        if (error instanceof ApiError && (error.status === 403 || error.status === 401 || error.status === 404 || error.isBusinessRule)) return false;
        return count < 2;
      },
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <I18nProvider>
            <AuthProvider>
              <ToastProvider>
                <App />
              </ToastProvider>
            </AuthProvider>
          </I18nProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </StrictMode>,
);

/* شاشةٌ لم تُحمَّل بعد نشرٍ جديد ← إعادةُ تحميلٍ واحدة (انظر lib/app-update.ts) */
installPreloadRecovery();
installErrorReporter();

/* عاملُ الخدمة: في البناء المنشور وحده — في التطوير يخزّن ملفّاتٍ تتغيّر مع كل حفظ فيربك العمل.
   لا يخزّن أيَّ بيانات (انظر public/sw.js) — الغلافُ وحده ليُثبَّت التطبيق ويفتح بلا انتظار.
   ومراقبةُ تبدّله هي ما يُظهر شريطَ «صدرت نسخةٌ جديدة». */
if (import.meta.env.PROD) registerServiceWorker();
